import { capitalizeFirstLetter } from '../utilities';

export const HandleError = (errorData: any): string => {
  const errors = errorData?.errors;

  if (!errors) {
    return '';
  }

  return formatError(errors);
};

// The message, followed by the reason the backend gave for the request failing, if any
export const withErrorReason = (message: string, error: any): string => {
  const reason = HandleError(error?.response?.data);

  return reason ? `${message}: ${reason}` : message;
};

const formatError = (error: any): string => {
  if (typeof error === 'string') {
    return translateStandaloneError(error);
  }

  if (Array.isArray(error)) {
    return error.map(formatError).filter(Boolean).join('\n');
  }

  if (error?.code) {
    return translateStandaloneError(error.code);
  }

  if (typeof error === 'object' && error !== null) {
    return Object.entries(error)
      .map(([key, value]: [string, any]) => formatObjectError(key, value))
      .filter(Boolean)
      .join('\n');
  }

  return translateStandaloneError(error);
};

const formatObjectError = (key: string, value: any): string => {
  const field = translateField(key);

  if (Array.isArray(value)) {
    return value
      .map((item) => `${field} ${formatErrorMessage(item)}`)
      .filter(Boolean)
      .join('\n');
  }

  return `${field} ${formatErrorMessage(value)}`;
};

const formatErrorMessage = (value: any): string => {
  if (typeof value === 'string') {
    return translateMessage(value);
  }

  if (value?.code) {
    return translateMessage(value.code);
  }

  if (value?.one) {
    return translateMessage(value.one);
  }

  if (value?.other) {
    return translateMessage(value.other);
  }

  if (Array.isArray(value)) {
    return value.map(formatErrorMessage).filter(Boolean).join('\n');
  }

  if (typeof value === 'object' && value !== null) {
    return Object.entries(value)
      .map(([nestedKey, nestedValue]: [string, any]) => {
        return `${translateField(nestedKey)} ${formatErrorMessage(nestedValue)}`;
      })
      .filter(Boolean)
      .join('\n');
  }

  return translateMessage(value);
};

const translateStandaloneError = (key: any): string => {
  const value = String(key);

  return capitalizeFirstLetter(errorDictionary[value] ?? translatePattern(value) ?? value);
};

// Backend errors containing values, so they cannot be looked up in the dictionary
const swedishHours = (hours: string): string => hours.replace('.', ',');

const errorPatterns: [RegExp, (...matches: string[]) => string][] = [
  [
    /^The budget cannot be lower than the hours already invoiced on this team agreement \((.+) h\)$/,
    (hours) =>
      `Timpotten kan inte bli lägre än de timmar som redan fakturerats på teamavtalet (${swedishHours(hours)} h)`,
  ],
  [
    /^The agreement's total budget cannot be lower than the hours already invoiced on the agreement \((.+) h\)$/,
    (hours) =>
      `Avtalets totala timpott kan inte bli lägre än de timmar som redan fakturerats på avtalet (${swedishHours(
        hours
      )} h)`,
  ],
];

const translatePattern = (value: string): string | undefined => {
  for (const [pattern, translate] of errorPatterns) {
    const match = value.match(pattern);

    if (match) {
      return translate(...match.slice(1));
    }
  }

  return undefined;
};

const translateField = (key: any): string => {
  const value = String(key);

  return capitalizeFirstLetter(errorDictionary[value] ?? value);
};

const translateMessage = (key: any): string => {
  const value = String(key);

  return errorDictionary[value] ?? value;
};

interface ErrorDictionary {
  [index: string]: string;
}

const errorDictionary: ErrorDictionary = {
  // Field names
  address: 'Gatuaddress',
  birth_date: 'Födelsedatum',
  city: 'Postort',
  create: 'skapande',
  date: 'datum',
  date_range: 'tidsperiod',
  delete: 'borttagning',
  edison_id: 'edison-ID',
  email: 'E-post adressen',
  first_name: 'Förnamn',
  fortnox_id: 'fortnox_id',
  invoice_detail: 'fakturauppgift',
  invoice_detail_errors: 'faktura felmeddelande: ',
  mobile: 'Mobilnummer',
  name: 'Namn',
  org_number: 'org-nummer',
  electronic_reference: 'elektroniskreferens',
  password: 'lösenord',
  password_change: 'lösenordbyte',
  person: 'Vårdnadshavare/nödkontakt',
  'person.mobile': 'Telefon för vårdnadshavare/nödkontakt',
  'people.mobile': 'Något telefon',
  'people.email': 'Något epostadress',
  person_nr: 'Personnummer',
  price_periods: 'prisperioder',
  recipient: 'mottagare',
  residence: 'boende',
  'invoice_detail.org_number': 'org-nummer',
  residence_group_errors: 'avtalspart felmeddelande: ',
  salary_detail: 'Clearingnummer eller kontonummer',
  'salary_detail.clearing_number': 'Clearingnummer',
  'salary_detail.account_number': 'Kontonummer',
  surname: 'Efternamn',
  tele: 'Hemtelefon',
  ung_intra_id: 'ung_intra_id',
  update: 'updatering',
  visiting_address: 'adress till boendet',
  zip_code: 'Postnummer',
  title: 'Titeln',
  team: 'Teamet',

  // Backend specific errors
  should_be_personnummer: 'skulle vara personnummer',
  "can't be blank": 'kan inte vara tom(t)',
  'must exist': 'kan inte vara tom(t)',
  'has already been taken': 'finns redan i systemet',
  'is invalid': 'är ogiltig(t)',
  'is an invalid number': 'är ogiltig(t)',
  'is too short (minimum is 1 character)': 'är för kort',
  'is too short (minimum is %{count} characters)': 'är för kort',
  too_short: 'är för kort',
  too_long: 'är för lång(t)',
  invalid_characters: 'har ogiltiga tecken',
  bad_checksum: 'har fel kontrollsumma',
  not_in_future: 'kan inte vara i framtiden',
  'must be within the agreements date range': 'Datumet måste ligga inom start- och slutdatum',
  'invalid organisation number': 'är ogiltigt',
  'do not match': 'matchar inte',
  'Zip code invalidated by ung intra': 'ogiltigt postnummer',
  'Invoice detail is invalid': 'faktura har ogiltiga uppgifter',
  'not successfull': 'misslyckades',
  'Validation failed:': 'misslyckades:',
  unknown_clearing_number: 'är ogiltig',
  'is too short (minimum is 8 characters)': 'ska vara mellan 8 och 20 tecken långt.',
  'is too long (maximum is 20 characters)': 'ska vara mellan 8 och 20 tecken långt.',
  'invalid password format':
    'ska vara mellan 8 och 20 tecken långt och innehålla stora och små bokstäver, siffror och specialtecken.',
  'Invalid e-mail.': 'Ogiltig e-post',
  '#<ActiveRecord::RecordInvalid: Validation failed: Zip code invalidated by ung intra>': 'Felaktigt postnummer.',
  '#<ActiveRecord::RecordInvalid: Validation failed: Invoice detail is invalid>':
    'Något är inte korrekt med fakturauppgifterna, kanske är postnumret inte giltigt.',
  'invalidated by ung intra': 'är ogiltigt',
  'cannot overlap with another agreement of the same residence':
    'kan inte överlappa med ett annat avtal för samma boende',

  // Agreement correction errors
  'Team agreement must be active': 'Avtalet måste vara aktivt',
  'Budgeted hours must be greater than 0': 'Timpotten måste vara större än 0',
  'The team agreement is deactivated and cannot be corrected': 'Teamavtalet är inaktivt och kan inte korrigeras',

  // Invoice deletion errors
  'An invoice sent to Fortnox cannot be deleted': 'En faktura som skickats till Fortnox kan inte tas bort',
  'A later invoice of the agreement must be deleted first': 'En senare faktura för avtalet måste tas bort först',
  'A split shift of this invoice is already invoiced on the following agreement':
    'Ett uppdelat pass på fakturan är redan fakturerat på nästa avtal',
  'The person is already an employee': 'Personen är redan anställd som deltagare eller teamledare',

  // UngIntra errors
  'Duplicate email': 'E-post finns redan i systemet',
  'Duplicate personNr.':
    'En person med angiven personnummer finns redan i databasen och arbetar i ett annat team. OBS. Lägg inte till personen igen utan kontakta info@ungomsorg.se',
  'E-mail already in use.': 'e-post finns redan i systemet',

  // Other app-specific errors
  email_duplicated:
    'kan inte läggas till då användaren redan finns i systemet. Lägg inte till personen igen, utan återaktivera dennes gamla profil. Vid frågor kontakta info@ungomsorg.se',
  interview: 'intervju',
  more_than_max_trainings: 'kan inte ge en användare mer än maximal mängd tillåtna upplärningstillfällen',
  fortnox_error: 'inloggning i Fortnox krävs.',
  fortnox_unknown_error: 'något gick fel med Fortnox.',
  fortnox_residence_error: 'Kontakta admin.',

  // Register request errors
  existing_invalid_request: 'Det finns redan en RegU-begäran som inte har hanterats.',
  existing_valid_request: 'Det finns redan en godkänd och giltig RegU-begäran.',
  request_user_under_14_years_old:
    'Användaren är under 14 år - förfrågan kommer sparas som undantagen (under 14 år) automatiskt. Vill du ändå skapa en förfrågan?',

  // Token errors
  'Token expired or invalid':
    "Din förfrågan för att återställa lösenord har gått ut. \r\nVänligen tryck på 'Glömt lösenord' igen och följ instruktionerna.",
};
